import type { FC } from 'react'
import React, { useMemo } from 'react'
import { XIcon } from '@heroicons/react/outline'

import styles from './index.module.css'
import { useTranslation } from 'react-i18next'
import Button from '@components/Button'
import { IconX } from '@tabler/icons-react'
import SaveToReportButton from '@components/SelectedFilters/SaveToReportButton'
import Tooltip from '@components/Tooltip'

// Filter value can be a string or array of strings (for multi-select)
type FilterValue = string | string[]

interface SelectedFiltersProps {
  filters: Record<string, FilterValue>
  showSaveToReportButton?: boolean

  clearFilter(key: string, value?: string): void

  clearAllFilters(): void
}

const SelectedFilters: FC<SelectedFiltersProps> = ({
  filters,
  clearFilter,
  clearAllFilters,
  showSaveToReportButton = false,
}) => {
  const { t } = useTranslation('common')

  // Flatten filters for display - each value in an array becomes a separate badge
  const flattenedFilters = useMemo(() => {
    const result: Array<{ key: string; value: string; isArrayItem: boolean }> = []

    Object.entries(filters).forEach(([key, value]) => {
      if (!value) return

      if (Array.isArray(value)) {
        value.forEach((v) => {
          if (v) {
            result.push({ key, value: v, isArrayItem: true })
          }
        })
      } else if (value) {
        result.push({ key, value, isArrayItem: false })
      }
    })

    return result
  }, [filters])

  if (!flattenedFilters.length) return null

  const renderFilterLabel = (key: string) => {
    if (key === 'jobRole') return `${t('jobTitle')}: `
    if (key === 'completionDate') return `${t('completionDate')}: `
    if (key === 'id') return 'ID: '
    if (key === 'publicFormRequestorEmail') return 'Requestor: '
    if (key === 'pipelineProcessName') return `${t('workflow', { defaultValue: 'Workflow' })}: `
    if (key === 'workflowReference') return `${t('workflowId')}: `
    // `filters.workflow` is the chosen intake form record (matches the
    // "Intake form" column in the table) — column-header picks only.
    if (key === 'workflow') return 'Intake form: '
    // `filters.workflowContext` is the top "Select a form to update
    // columns" pick. Prefix uses a dash so it reads as a compound label
    // ("Workflow - form") rather than colliding with the table's
    // "Workflow" column header. The value itself is
    // "pipeline_name - stage_name" (the Workflow + Intake form column
    // values dash-joined), so the full chip reads:
    //   `Workflow - form: <pipeline_name> - <stage_name>`
    if (key === 'workflowContext') return 'Workflow - form: '
    if (key === 'tag') return `${t('tag', { defaultValue: 'Tag' })}: `
    if (key === 'actionRequired' || key === 'action_required') return ''
    if (key === 'nameOptions') return 'Name: '
    if (key === 'emailOptions') return 'Email: '
    if (key === 'status') return 'Status: '
    if (key === 'source') return 'Source: '
    if (key.startsWith('fieldDate_')) return `${key.replace('fieldDate_', '')}: `
    if (key.startsWith('field_')) return `${key.replace('field_', '')}: `
    return `${key}: `
  }

  return (
    <div className={styles.container}>
      <div className="font-bold">{t('selectedFilters')}:</div>
      {flattenedFilters.map(({ key, value, isArrayItem }, index) => (
        <div key={`${key}-${value}-${index}`} className={styles.badge}>
          <p className={styles.filterValue}>
            {renderFilterLabel(key)}
            <span className="max-h-6 bg-white rounded-lg px-2 py-0.5">{value}</span>
          </p>
          <button className={styles.clearFilterIcon} onClick={() => clearFilter(key, isArrayItem ? value : undefined)}>
            <XIcon width={14} />
          </button>
        </div>
      ))}
      <div className="flex gap-1.5">
        {showSaveToReportButton && <SaveToReportButton />}
        <Button
          color="white"
          onClick={clearAllFilters}
          className="w-6 min-w-0"
          data-tooltip-content={t('clearAllLabel')}
          data-tooltip-id="clear_tooltip"
        >
          <IconX className="w-3 h-3" stroke={3} />
        </Button>
        <Tooltip id="clear_tooltip" place="bottom" className="z-10" />
      </div>
    </div>
  )
}

export default SelectedFilters
